import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import Joi from 'joi';

import { useCreateActionConditionMutation, useUpdateActionConditionMutation } from 'api/main';
import {
    editingConditionIdSet,
    editingConditionSet,
    newActionConditionUpdated,
    newEditingConditionIdSet,
} from 'reducers/customTunnel';
import { customTunnelSelectors } from 'reducers';
import { conditionType } from 'utils/constants';
import { validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { CustomSelect, CustomTextField, Loader } from 'components';
import { Box, MenuItem, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';

const schema = {
    type: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
    limitValue: Joi.number().required().messages({ 'number.base': texts.SAISIR_LE_CHAMP }),
};

const ConditionForm = ({ onCancel, condition, index, editingAction }) => {
    const dispatch = useDispatch();
    const form = useSelector(customTunnelSelectors.getEditingCondition);
    const newAction = useSelector(customTunnelSelectors.getNewAction);
    const { ppId: ppActionId, pmId: pmActionId } = useSelector(
        customTunnelSelectors.getEditingActionId
    );
    const [errors, setErrors] = useState({ type: '', limitValue: '' });

    const [
        createCondition,
        { isLoading: createLoading, isSuccess: createSuccess, reset: createReset },
    ] = useCreateActionConditionMutation();
    const [
        updateCondition,
        { isLoading: updateLoading, isSuccess: updateSuccess, reset: updateReset },
    ] = useUpdateActionConditionMutation();

    const typeOptions = useMemo(() => {
        if (newAction) {
            if (newAction.conditions.length === 1) {
                return Object.keys(conditionType);
            }
            if (newAction.conditions.find(c => c.type === conditionType.GREATER_THAN))
                return ['LESS_THAN'];
            if (newAction.conditions.find(c => c.type === conditionType.LESS_THAN))
                return ['GREATER_THAN'];
        } else {
            if (editingAction.conditions.length === 1) {
                return Object.keys(conditionType);
            }
            if (editingAction.conditions.find(c => c.type === conditionType.GREATER_THAN))
                return ['LESS_THAN'];
            if (editingAction.conditions.find(c => c.type === conditionType.LESS_THAN))
                return ['GREATER_THAN'];
        }
        return Object.keys(conditionType);
    }, [editingAction?.conditions, newAction]);

    const handleChange = useCallback(
        key => e => {
            setErrors(prevState => ({ ...prevState, [key]: '' }));
            dispatch(editingConditionSet({ key, value: e.target.value }));
        },
        [dispatch]
    );

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            if (condition?.id) {
                updateCondition({
                    type: form.type,
                    limitValue: +form.limitValue,
                    ppId: condition.id,
                    pmId: condition.pmId,
                });
            } else if (ppActionId) {
                createCondition({ ...form, ppActionId, pmActionId });
            } else {
                dispatch(
                    newActionConditionUpdated({
                        conditionIndex: index,
                        value: form,
                    })
                );
                dispatch(editingConditionSet(null));
                dispatch(newEditingConditionIdSet(null));
            }
        } else {
            setErrors(newErrors);
        }
    }, [
        condition,
        createCondition,
        dispatch,
        form,
        index,
        pmActionId,
        ppActionId,
        updateCondition,
    ]);

    useEffect(() => {
        if (form?.type && !typeOptions.find(opt => conditionType[opt] === form?.type)) {
            // make sure there's always something selected for condition type
            dispatch(editingConditionSet({ key: 'type', value: conditionType[typeOptions[0]] }));
        }
    }, [dispatch, form?.type, typeOptions]);

    useEffect(() => {
        if (createSuccess) {
            dispatch(editingConditionSet(null));
            dispatch(newEditingConditionIdSet(null));
            createReset();
        }
    }, [createReset, createSuccess, dispatch]);

    useEffect(() => {
        if (updateSuccess) {
            dispatch(editingConditionSet(null));
            dispatch(editingConditionIdSet(null));
            updateReset();
        }
    }, [dispatch, updateReset, updateSuccess]);

    return (
        <Box textAlign="center">
            <Box display="flex" alignItems="flex-start" gap={1} paddingY={2}>
                <Typography sx={{ mt: 1 }}>{texts.IF_SUSCRIBER_SCORE}</Typography>
                <CustomSelect
                    controlSx={{ mb: 0, minWidth: '150px' }}
                    value={form.type}
                    onChange={handleChange('type')}
                    error={!!errors.type}
                    helperText={errors.type}
                >
                    {typeOptions.map(key => (
                        <MenuItem key={key} value={conditionType[key]}>
                            {texts.ALERTS[key]}
                        </MenuItem>
                    ))}
                </CustomSelect>
                <CustomTextField
                    type="number"
                    allowNegative
                    formControlProps={{ sx: { mb: 0, minWidth: '150px' } }}
                    value={form.limitValue}
                    onChange={handleChange('limitValue')}
                    error={errors.limitValue}
                />
            </Box>

            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <icons.CloseThick
                    sx={{ cursor: 'pointer', '&:hover': { color: 'error.main' } }}
                    onClick={onCancel}
                />
                <Loader loading={updateLoading || createLoading} size={20} marginTop={0}>
                    <icons.CheckBold
                        sx={{ cursor: 'pointer', '&:hover': { color: 'success.main' } }}
                        onClick={handleSubmit}
                    />
                </Loader>
            </Box>
        </Box>
    );
};

ConditionForm.propTypes = {
    onCancel: PropTypes.func,
    condition: PropTypes.object,
    index: PropTypes.number,
    editingAction: PropTypes.object,
};

export default ConditionForm;
